import environment from '@department-of-veterans-affairs/platform-utilities/environment';
import footerContent from 'platform/forms/components/FormFooter';
import { minimalHeaderFormConfigOptions } from 'platform/forms-system/src/js/patterns/minimal-header';

import manifest from '../manifest.json';
import IntroductionPage from '../containers/IntroductionPage';
import ConfirmationPage from '../containers/ConfirmationPage';
import getHelp from '../../shared/components/GetFormHelp';
import transformForSubmit from './submit-transformer';
import prefillTransformer from './prefill-transformer';
import { TITLE, SUBTITLE, FORM_ID } from '../definitions/constants';
import { getDefaultVhaFacilityState } from '../definitions/facilityStates';

import mailingAddress from '../pages/mailingAddress';
import phoneAndEmailAddress from '../pages/phoneAndEmailAddress';
import benefitStatus from '../pages/benefitStatus';
import vhaMedicalFacility from '../pages/vhaMedicalFacility';
import personalInformation from '../pages/personalInformation';
import benefitTerminationCertification from '../pages/benefitTerminationCertification';
import applicationCertification from '../pages/applicationCertification';
import deviceApplianceMedicationPages from '../pages/deviceApplianceMedicationDetails';

const onlyWhenContinuing = formData =>
  formData?.electTermination === 'continue';

export const defaultFacilityStateFromMailingAddress = (
  previousData = {},
  nextData = {},
) => {
  const previousMailingState = getDefaultVhaFacilityState(
    previousData?.address,
  );
  const nextMailingState = getDefaultVhaFacilityState(nextData?.address);
  const currentFacilityState = nextData?.vhaMedicalFacilityState;

  const shouldUseMailingState =
    !currentFacilityState || currentFacilityState === previousMailingState;

  if (!shouldUseMailingState) {
    return nextData;
  }

  const facilityStateChanged =
    currentFacilityState && currentFacilityState !== nextMailingState;

  return {
    ...nextData,
    vhaMedicalFacilityState: nextMailingState || undefined,
    ...(facilityStateChanged
      ? {
          vhaMedicalFacility: undefined,
          vhaMedicalFacilityOther: undefined,
        }
      : {}),
  };
};

export const clearVhaMedicalFacilityWhenStateChanges = (
  previousData = {},
  nextData = {},
) => {
  const previousState = previousData?.vhaMedicalFacilityState;
  const nextState = nextData?.vhaMedicalFacilityState;

  if (previousState && previousState !== nextState) {
    return {
      ...nextData,
      vhaMedicalFacility: undefined,
      vhaMedicalFacilityOther: undefined,
    };
  }

  return nextData;
};

const gatedDeviceApplianceMedicationPages = Object.fromEntries(
  Object.entries(deviceApplianceMedicationPages).map(([key, page]) => {
    const originalDepends = page.depends;
    return [
      key,
      {
        ...page,
        depends: (formData, index) => {
          if (!onlyWhenContinuing(formData)) return false;
          return typeof originalDepends === 'function'
            ? originalDepends(formData, index)
            : true;
        },
      },
    ];
  }),
);

/** @type {FormConfig} */
const formConfig = {
  rootUrl: manifest.rootUrl,
  urlPrefix: '/',
  submitUrl: `${environment.API_URL}/simple_forms_api/v1/simple_forms`,
  trackingPrefix: 'clothing-allowance-10-8678-',
  introduction: IntroductionPage,
  confirmation: ConfirmationPage,
  formId: FORM_ID,
  formOptions: {
    useWebComponentForNavigation: true,
  },
  prefillEnabled: true,
  prefillTransformer,
  transformForSubmit,
  preSubmitInfo: {
    statementOfTruth: {
      body: 'I confirm that the identifying information in this form is accurate and has been represented correctly.',
      messageAriaDescribedby:
        'I certify that the information I provided is true and correct.',
      fullNamePath: 'fullName',
    },
  },
  dev: {
    showNavLinks: true,
    collapsibleNavLinks: true,
    disableWindowUnloadInCI: true,
  },
  saveInProgress: {
    messages: {
      inProgress: 'Your annual clothing allowance application is in progress.',
      expired:
        'Your saved annual clothing allowance application has expired. If you want to apply for annual clothing allowance, please start a new application.',
      saved: 'Your annual clothing allowance application has been saved.',
    },
  },
  version: 0,
  hideUnauthedStartLink: true,
  savedFormMessages: {
    notFound: 'Please start over to apply for annual clothing allowance.',
    noAuth:
      'Please sign in again to continue your application for annual clothing allowance.',
  },
  title: TITLE,
  subTitle: SUBTITLE,
  defaultDefinitions: {},
  chapters: {
    personalInformationChapter: {
      title: 'Veteran’s basic information',
      pages: {
        personalInformation: {
          path: 'personal-information',
          title: 'Your name and Social Security number',
          uiSchema: personalInformation.uiSchema,
          schema: personalInformation.schema,
        },
      },
    },
    mailingInformationChapter: {
      title: 'Mailing address',
      pages: {
        mailingAddress: {
          path: 'mailing-address',
          title: 'Your mailing address',
          uiSchema: mailingAddress.uiSchema,
          schema: mailingAddress.schema,
          updateFormData: defaultFacilityStateFromMailingAddress,
        },
      },
    },
    contactInformationChapter: {
      title: 'Contact information',
      pages: {
        phoneAndEmailAddress: {
          path: 'phone-and-email-address',
          title: 'Your phone and email address',
          uiSchema: phoneAndEmailAddress.uiSchema,
          schema: phoneAndEmailAddress.schema,
        },
      },
    },
    benefitStatusChapter: {
      title: 'Benefit status',
      pages: {
        benefitStatus: {
          path: 'benefit-status',
          title: 'No longer need a clothing allowance?',
          uiSchema: benefitStatus.uiSchema,
          schema: benefitStatus.schema,
        },
      },
    },
    vhaMedicalFacilityChapter: {
      title: 'VHA medical facility',
      pages: {
        vhaMedicalFacility: {
          path: 'vha-medical-facility',
          title: 'Select your local Prosthetic and Sensory Aids Service',
          uiSchema: vhaMedicalFacility.uiSchema,
          schema: vhaMedicalFacility.schema,
          updateFormData: clearVhaMedicalFacilityWhenStateChanges,
        },
      },
    },
    deviceApplianceMedicationChapter: {
      title: 'Device, appliance, and medication details',
      pages: {
        ...gatedDeviceApplianceMedicationPages,
      },
    },
    applicationCertificationChapter: {
      title: 'Application certification',
      pages: {
        applicationCertification: {
          path: 'required-certifications',
          title: 'Application certification statement, legal information',
          depends: onlyWhenContinuing,
          uiSchema: applicationCertification.uiSchema,
          schema: applicationCertification.schema,
        },
      },
    },
    benefitTerminationCertificationChapter: {
      title: 'Benefit termination',
      pages: {
        benefitTerminationCertification: {
          path: 'benefit-termination-certification',
          title: 'Benefit termination confirmation and legal information',
          depends: formData =>
            !formData?.electTermination ||
            formData.electTermination === 'terminate',
          uiSchema: benefitTerminationCertification.uiSchema,
          schema: benefitTerminationCertification.schema,
        },
      },
    },
  },

  ...minimalHeaderFormConfigOptions({
    breadcrumbList: [
      {
        href: '/',
        label: 'VA.gov home',
      },
      {
        href: '/forms/',
        label: 'VA forms',
      },
      {
        href: '/forms/10-8678/',
        label: 'Application for annual clothing allowance',
      },
      {
        href: '/forms/10-8678/apply-annual-clothing-allowance/',
        label: 'Apply for annual clothing allowance online',
      },
    ],
    wrapping: true,
  }),
  getHelp,
  footerContent,
};

export default formConfig;
